'use client';

import Link from '~/(tenant)/_components/tenant-link';
import { useLinkStatus } from 'next/link';
import { usePathname, useRouter } from 'next/navigation';

import { Capabilities } from '@kit/capabilities';
import { useCapabilityOr } from '@kit/capabilities/client';
import {
  Calendar,
  CalendarPlus,
  CreditCard,
  Dumbbell,
  Home,
  IdCard,
  type LucideIcon,
  Mail,
  Package,
  ShoppingBag,
  User,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';

import { Trans } from '@kit/ui/trans';
import { useIsMobile } from '@kit/ui/use-mobile';

import { pickLocalized } from '~/(tenant)/_components/native/_lib/use-localized';
import { useNativeTokens } from '~/(tenant)/_components/native/_lib/use-native-tokens';
import {
  SAFE_AREA_INSET,
  useSystemBarRail,
} from '~/(tenant)/_components/native/_lib/use-system-bar-rail';
import { useIsNative } from '~/(tenant)/_lib/capacitor/capacitor-context';
import {
  type TabId,
  type TabsConfig,
} from '~/(tenant)/_lib/schema/native-home-config.schema';
import pathsConfig from '~/config/paths.config';
import { NATIVE_TAB_PATHS, defaultNativeTabs } from './_lib/native-tab-routes';
import { useNativeTabBarContainer } from './native-navigation';
import { useSession } from '~/lib/hooks/use-header-auth';

interface TabSpec {
  id: TabId;
  labelKey: string;
  defaultLabel: string;
  icon: LucideIcon;
  path: string;
  authRequired: boolean;
}

const TAB_SPECS: Record<TabId, TabSpec> = {
  home: {
    id: 'home',
    labelKey: 'common:routes.home',
    defaultLabel: 'Home',
    icon: Home,
    path: NATIVE_TAB_PATHS.home,
    authRequired: false,
  },
  book: {
    id: 'book',
    labelKey: 'common:bookTab',
    defaultLabel: 'Book',
    icon: CalendarPlus,
    path: NATIVE_TAB_PATHS.book,
    authRequired: false,
  },
  bookings: {
    id: 'bookings',
    labelKey: 'common:bookings',
    defaultLabel: 'Bookings',
    icon: Calendar,
    path: NATIVE_TAB_PATHS.bookings,
    authRequired: true,
  },
  packages: {
    id: 'packages',
    labelKey: 'common:packages',
    defaultLabel: 'Packages',
    icon: Package,
    path: NATIVE_TAB_PATHS.packages,
    authRequired: false,
  },
  profile: {
    id: 'profile',
    labelKey: 'common:routes.profile',
    defaultLabel: 'Profile',
    icon: User,
    path: NATIVE_TAB_PATHS.profile,
    authRequired: true,
  },
  membership: {
    id: 'membership',
    labelKey: 'common:membership',
    defaultLabel: 'Membership',
    icon: CreditCard,
    path: NATIVE_TAB_PATHS.membership,
    authRequired: false,
  },
  memberships: {
    id: 'memberships',
    labelKey: 'common:myMemberships',
    defaultLabel: 'My Memberships',
    icon: IdCard,
    path: NATIVE_TAB_PATHS.memberships,
    authRequired: false,
  },
  coaches: {
    id: 'coaches',
    labelKey: 'coaches:title',
    defaultLabel: 'Coaches',
    icon: Dumbbell,
    path: NATIVE_TAB_PATHS.coaches,
    authRequired: false,
  },
  shop: {
    id: 'shop',
    labelKey: 'common:shop',
    defaultLabel: 'Shop',
    icon: ShoppingBag,
    path: NATIVE_TAB_PATHS.shop,
    authRequired: false,
  },
  contact: {
    id: 'contact',
    labelKey: 'common:contact',
    defaultLabel: 'Contact',
    icon: Mail,
    path: NATIVE_TAB_PATHS.contact,
    authRequired: false,
  },
};

export function MobileBottomTabBar({
  enabled,
  tabsConfig,
}: {
  enabled: boolean;
  /** Tenant tab configuration. When omitted, the default 5-tab layout is used. */
  tabsConfig?: TabsConfig;
}) {
  const t = useNativeTokens();
  const { isNative, isReady } = useIsNative();
  const tabBarContainer = useNativeTabBarContainer();
  const isMobile = useIsMobile();
  const rail = useSystemBarRail();
  const pathname = usePathname();
  const hasPackages = useCapabilityOr(Capabilities.PACKAGES, true);
  const router = useRouter();
  const { data: session } = useSession();
  const { i18n, t: tt } = useTranslation();
  const locale = i18n.language;
  const isAuthenticated = !!session;

  // Show for Capacitor native or mobile web when enabled
  // Hide on membership page — it has its own full-page flow
  if (
    !enabled ||
    !isReady ||
    (!isNative && !isMobile) ||
    pathname === '/membership' ||
    pathname.startsWith('/membership/')
  ) {
    return null;
  }

  // Resolve which tabs to render — fallback to defaults if config absent.
  const resolvedItems: Array<{
    id: TabId;
    label?: string;
    label_ar?: string;
    hidden?: boolean;
  }> = tabsConfig?.items ?? defaultNativeTabs(hasPackages).map((id) => ({ id }));
  const visibleItems = resolvedItems.filter((item) => !item.hidden);

  const showLabels = tabsConfig?.showLabels ?? true;
  const isGlass = tabsConfig?.style === 'glass';

  const isActive = (path: string) => {
    if (path === '/') return pathname === '/';
    return pathname === path || pathname.startsWith(`${path}/`);
  };

  const handleTabClick = (
    path: string,
    authRequired: boolean,
    e: React.MouseEvent,
  ) => {
    if (authRequired && !isAuthenticated) {
      e.preventDefault();
      // Keep the current sign-in/sign-up flow and its original return destination.
      if (pathname === pathsConfig.auth.signIn || pathname === pathsConfig.auth.signUp) return;
      router.push(`${pathsConfig.auth.signIn}?next=${path}`);
    }
  };

  const navLabel = tt('common:memberClub.nav.mainNavigationAriaLabel', {
    defaultValue: 'Main navigation',
  });

  const tabs = visibleItems.map((item) => {
    const spec = TAB_SPECS[item.id];
    const active = isActive(spec.path);
    const Icon = spec.icon;
    const customLabel = pickLocalized(item.label, item.label_ar, locale);
    const label = customLabel ? (
      customLabel
    ) : (
      <Trans i18nKey={spec.labelKey} defaults={spec.defaultLabel} />
    );

    return (
      <Link
        key={spec.id}
        href={spec.path}
        prefetch={isNative && pathname === '/' && spec.id === 'book' ? true : undefined}
        data-native-navigation="tab"
        onClick={(e) => handleTabClick(spec.path, spec.authRequired, e)}
        aria-current={active ? 'page' : undefined}
        data-test={`tab-${spec.id}`}
        style={
          rail
            ? {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 48,
                height: 48,
                borderRadius: 999,
                position: 'relative',
                color: active ? t.foreground : t.muted,
                backgroundColor: active
                  ? `color-mix(in srgb, ${t.foreground} 12%, transparent)`
                  : 'transparent',
                textDecoration: 'none',
              }
            : {
                display: 'flex',
                flex: 1,
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
                padding: '8px 2px 0',
                minHeight: 44,
                minWidth: 44,
                position: 'relative',
                color: active ? t.foreground : t.muted,
                textDecoration: 'none',
              }
        }
      >
        <TabPendingIndicator color={t.foreground} />
        <Icon size={rail ? 22 : 20} strokeWidth={active ? 1.6 : 1.3} aria-hidden />
        {rail ? (
          // The rail is a narrow column, so labels stay available to assistive tech only.
          <span className="sr-only">{label}</span>
        ) : (
          showLabels && (
            <span
              style={{
                fontFamily: t.fontSans,
                fontSize: 10,
                letterSpacing: '0.03em',
                textTransform: 'uppercase',
                fontWeight: active ? 600 : 400,
                // Keep multi-word labels (e.g. "My Bookings") on one line;
                // the tab is flex:1 so it has room once side padding is tight.
                whiteSpace: 'nowrap',
                maxWidth: '100%',
              }}
            >
              {label}
            </span>
          )
        )}
        {/* Active indicator: small dot below label, in foreground tone */}
        {active && !rail && (
          <div
            style={{
              width: 3,
              height: 3,
              borderRadius: 999,
              backgroundColor: t.foreground,
              marginTop: 2,
            }}
            aria-hidden
          />
        )}
      </Link>
    );
  });

  const surface = {
    backgroundColor: isGlass ? `${t.background}EB` : t.background,
    backdropFilter: isGlass ? 'blur(20px) saturate(160%)' : undefined,
    WebkitBackdropFilter: isGlass ? 'blur(20px) saturate(160%)' : undefined,
  };

  // iPhone Duo moves the system bars into a vertical column on one edge. Native
  // tab bars follow it as a floating pill, so the HTML tab bar does the same:
  // it sits inside that edge's safe-area column, clear of the status items
  // at the top of the column.
  const tabBar = rail ? (
    <nav
      dir={i18n.dir(locale)}
      aria-label={navLabel}
      className="fixed top-0 bottom-0 z-50 flex items-center justify-center"
      data-native-tab-bar=""
      data-layout="rail"
      style={{
        [rail]: 0,
        width: SAFE_AREA_INSET[rail],
        pointerEvents: 'none',
        transform: 'translateZ(0)',
        WebkitTransform: 'translateZ(0)',
      }}
      data-test="mobile-bottom-tab-bar"
    >
      <div
        style={{
          ...surface,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 4,
          padding: 6,
          borderRadius: 999,
          border: `1px solid ${t.line}`,
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)',
          pointerEvents: 'auto',
        }}
      >
        {tabs}
      </div>
    </nav>
  ) : (
    <nav
      dir={i18n.dir(locale)}
      aria-label={navLabel}
      className="fixed right-0 bottom-0 left-0 z-50"
      // Booking scroll helpers treat this as the bottom of the visible page.
      data-native-tab-bar=""
      style={{
        // Subtle hairline above the bar — uses tenant's text-color line token.
        borderTop: `1px solid ${t.line}`,
        ...surface,
        paddingBottom: `calc(${SAFE_AREA_INSET.bottom} + 4px)`,
        paddingLeft: SAFE_AREA_INSET.left,
        paddingRight: SAFE_AREA_INSET.right,
        // iOS 26 WKWebView workaround: force a compositor layer so the bar
        // pins to the visual viewport instead of drifting on scroll-direction
        // changes (WebKit #297779). `contain: layout` prevents layout recalcs
        // in the scrolling container from leaking into the fixed bar.
        transform: 'translateZ(0)',
        WebkitTransform: 'translateZ(0)',
        willChange: 'transform',
        contain: 'layout',
      }}
      data-test="mobile-bottom-tab-bar"
    >
      <div
        style={{
          display: 'flex',
          height: 64,
          alignItems: 'center',
          justifyContent: 'space-around',
        }}
      >
        {tabs}
      </div>
    </nav>
  );

  if (isNative) return tabBarContainer ? createPortal(tabBar, tabBarContainer) : null;

  return tabBar;
}

/**
 * Tabs other than Book are not prefetched until touched, so a tap can wait on
 * the server. Show a thin bar on the tapped tab, delayed 100 ms so fast
 * navigations never flash it (Next.js "Slow networks" pattern).
 */
function TabPendingIndicator({ color }: { color: string }) {
  const { pending } = useLinkStatus();

  return (
    <span
      aria-hidden
      style={{
        position: 'absolute',
        top: 0,
        left: '30%',
        right: '30%',
        height: 2,
        borderRadius: 1,
        backgroundColor: color,
        opacity: pending ? 1 : 0,
        transition: pending ? 'opacity 150ms ease 100ms' : 'none',
      }}
    />
  );
}
